import { Reservation } from "shared/reservation"
import {
  ModuleMappingEntry,
  TemplateAssociationLike,
  orderedV2Modules,
} from "./lockerModuleOrder"

// A single printable line of the driver deposit sheet.
export type DepositReportRow = {
  doorLabel: string
  name: string
  depositCode: string
}

// Pending drop-off rows grouped under one locker module (e.g. "Module A").
export type DepositReportModule = {
  moduleName: string
  rows: DepositReportRow[]
}

// Localized strings the print document needs. The printed sheet is a separate
// browser document, so it cannot call `t()` itself — the caller resolves the
// labels and passes them in.
export type DepositReportLabels = {
  title: string
  door: string
  name: string
  depositCode: string
  total: string
  empty: string
  // "Generated at" label + the caller-formatted timestamp. The print doc can't
  // call `t()` or format dates in the operator's locale, so both come from the
  // caller (see the note above).
  generatedAt: string
  generatedAtValue: string
}

// Minimal shape of a locker grid entry we need for door→module mapping.
type GridEntry = {
  cells_group?: ({ door_id?: string | null } | null)[] | null
}

// Minimal structural shape of the fields we read off a V2 (`Sst2Locker`) locker.
// Typed structurally (like GridEntry above) so this module stays free of the
// heavier sstv2Types import — the caller passes a real `Sst2Locker`, which is
// assignable to this subset.
type V2GridSource = {
  doors?: { id: string; locker_group: number }[] | null
  templates_association?: (TemplateAssociationLike | null)[] | null
}

// Adapt a V2 locker's template/door geometry to the `GridEntry[]` shape that
// `buildDepositReportModules` consumes — crucially in the SAME order the V2
// dashboard uses to assign its A/B/C module letters, so the printed sheet and
// the on-screen signage agree.
//
// Both go through `orderedV2Modules`: sort by `visual_order`, drop
// `template`-less entries, then permute by the locker's saved `moduleMapping`
// (LOCKER-390). The resulting position becomes the module letter (index 0 → A,
// 1 → B, …). A door belongs to the template at its ORIGINAL `templates_association`
// index, given by `door.locker_group` — which is why the ordering helper carries
// that index through the sort.
export const buildV2DepositGrids = (
  sst2Locker: V2GridSource | null | undefined,
  moduleMapping?: ModuleMappingEntry[] | null,
): GridEntry[] => {
  const doors = sst2Locker?.doors || []
  return orderedV2Modules(sst2Locker, moduleMapping).map(({ originalIndex }) => ({
    cells_group: doors
      .filter((d) => d.locker_group === originalIndex)
      .map((d) => ({ door_id: d.id })),
  }))
}

// "Pending drop-off" = the parcel still needs to be deposited by the driver:
// the reservation is CONFIRMED but no `delivery_time` has been recorded yet.
// Once deposited, SST flips the status to DELIVERED and stamps delivery_time,
// so those naturally drop off this list.
//
// Rows are grouped by the locker module that owns each door. The module a door
// belongs to is the index of the `locker_grid` entry whose `cells_group`
// references that door_id — the same index the dashboard turns into A/B/C/…
export const buildDepositReportModules = (
  reservations: Reservation[],
  lockerGrid: GridEntry[] = [],
  moduleWord = "Module",
): DepositReportModule[] => {
  const doorToModule = new Map<string, number>()
  lockerGrid.forEach((g, i) =>
    (g?.cells_group || []).forEach((c) => {
      const did = c?.door_id
      if (did) doorToModule.set(did, i)
    }),
  )

  const UNKNOWN = Number.MAX_SAFE_INTEGER
  const groups = new Map<number, DepositReportRow[]>()

  reservations
    .filter(
      (r) =>
        r.sstReservation?.status === "CONFIRMED" &&
        !r.sstReservation?.delivery_time,
    )
    .forEach((r) => {
      const doorId = r.sstReservation?.door?.door_id
      const idx =
        doorId && doorToModule.has(doorId)
          ? (doorToModule.get(doorId) as number)
          : UNKNOWN
      const row: DepositReportRow = {
        doorLabel: r.sstReservation?.door?.door_label || "-",
        name:
          `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() ||
          r.email ||
          r.phone ||
          "-",
        depositCode: r.sstReservation?.delivery_code || "-",
      }
      if (!groups.has(idx)) groups.set(idx, [])
      ;(groups.get(idx) as DepositReportRow[]).push(row)
    })

  return [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([idx, rows]) => ({
      moduleName:
        idx === UNKNOWN
          ? moduleWord
          : `${moduleWord} ${String.fromCharCode(65 + idx)}`,
      // Sort by door so the driver walks each module top-to-bottom.
      rows: rows.sort((a, b) =>
        a.doorLabel.localeCompare(b.doorLabel, undefined, { numeric: true }),
      ),
    }))
}

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")

// Open a clean, print-styled window listing the pending-drop-off parcels,
// grouped by module, and trigger the browser print dialog. No backend / PDF
// dependency — the operator gets a paper sheet straight from the dashboard.
export const printDepositReport = (
  modules: DepositReportModule[],
  lockerName: string,
  lockerAddress: string,
  labels: DepositReportLabels,
  logoSrc = "",
): void => {
  const total = modules.reduce((n, m) => n + m.rows.length, 0)

  const renderRows = (rows: DepositReportRow[]): string =>
    rows
      .map(
        (r) =>
          `<tr>
            <td class="chk"><span class="box"></span></td>
            <td class="door">${escapeHtml(r.doorLabel)}</td>
            <td class="name">${escapeHtml(r.name)}</td>
            <td class="code"><span class="code-chip">${escapeHtml(
              r.depositCode,
            )}</span></td>
          </tr>`,
      )
      .join("")

  const sections = modules.length
    ? modules
        .map(
          (m) =>
            `<section class="module">
              <h2 class="module-title">${escapeHtml(m.moduleName)}</h2>
              <table>
                <thead>
                  <tr>
                    <th class="chk"></th>
                    <th class="door">${escapeHtml(labels.door)}</th>
                    <th>${escapeHtml(labels.name)}</th>
                    <th class="code">${escapeHtml(labels.depositCode)}</th>
                  </tr>
                </thead>
                <tbody>${renderRows(m.rows)}</tbody>
              </table>
            </section>`,
        )
        .join("")
    : `<p class="empty">${escapeHtml(labels.empty)}</p>`

  const addressLine = lockerAddress
    ? `<div class="address">${escapeHtml(lockerAddress)}</div>`
    : ""

  const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${escapeHtml(labels.title)}</title>
    <style>
      :root {
        --navy: #234285;
        --ink: #1a1a1a;
        --muted: #6b7280;
        --line: #e2e5ec;
        --zebra: #f7f9fc;
        --accent: #eef2ff;
      }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
          Helvetica, Arial, sans-serif;
        color: var(--ink);
        font-size: 13px;
        line-height: 1.4;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .sheet { max-width: 760px; margin: 0 auto; padding: 28px 32px; }

      .head {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 3px solid var(--navy);
        padding-bottom: 14px;
        margin-bottom: 22px;
      }
      .brand { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; }
      .logo-img { height: 40px; width: auto; }
      .logo {
        width: 36px; height: 36px; border-radius: 9px;
        background: var(--navy); color: #fff;
        display: flex; align-items: center; justify-content: center;
        font-weight: 800; font-size: 14px; letter-spacing: .5px;
      }
      .brand .kicker {
        font-size: 10px; text-transform: uppercase;
        letter-spacing: 1.5px; color: var(--muted); margin-bottom: 2px;
      }
      .brand h1 { margin: 0; font-size: 20px; color: var(--navy); font-weight: 800; }
      .brand .generated { font-size: 11px; color: var(--muted); margin-top: 3px; }
      .meta { text-align: right; }
      .meta .locker { font-size: 15px; color: var(--ink); font-weight: 700; }
      .meta .address { font-size: 12px; color: var(--muted); margin-top: 2px; max-width: 280px; margin-left: auto; }
      .meta .count {
        display: inline-block; margin-top: 8px;
        background: var(--accent); color: var(--navy);
        border-radius: 999px; padding: 3px 11px; font-weight: 700; font-size: 12px;
      }

      .module { margin-bottom: 22px; }
      .module-title {
        margin: 0 0 8px; font-size: 14px; font-weight: 700; color: var(--navy);
        text-transform: uppercase; letter-spacing: .6px;
        border-bottom: 2px solid var(--navy); padding-bottom: 5px;
      }

      table { width: 100%; border-collapse: collapse; }
      thead th {
        background: var(--navy); color: #fff; text-align: left;
        font-size: 11px; text-transform: uppercase; letter-spacing: .8px;
        padding: 8px 12px;
      }
      thead th.chk { width: 34px; }
      thead th.door { width: 70px; text-align: center; }
      thead th.code { text-align: center; width: 150px; }
      tbody td { padding: 10px 12px; border-bottom: 1px solid var(--line); vertical-align: middle; }
      tbody tr:nth-child(even) { background: var(--zebra); }
      td.chk { text-align: center; }
      td.chk .box {
        display: inline-block; width: 15px; height: 15px;
        border: 2px solid var(--navy); border-radius: 4px;
      }
      td.door { text-align: center; font-weight: 800; color: var(--navy); font-size: 15px; }
      td.name { font-weight: 600; }
      td.code { text-align: center; }
      .code-chip {
        font-family: "SF Mono", ui-monospace, "Courier New", monospace;
        font-size: 17px; font-weight: 700; letter-spacing: 2px;
        background: var(--accent); color: var(--navy);
        border-radius: 6px; padding: 3px 10px; display: inline-block;
      }
      .empty { text-align: center; color: var(--muted); font-style: italic; padding: 28px; }

      .foot {
        margin-top: 8px; padding-top: 10px; border-top: 1px solid var(--line);
        font-size: 10px; color: var(--muted);
      }

      @page { margin: 14mm; }
      @media print {
        .sheet { padding: 0; max-width: none; }
        thead { display: table-header-group; }
        tr { break-inside: avoid; }
        .module { break-inside: avoid; }
      }
    </style>
  </head>
  <body>
    <div class="sheet">
      <div class="head">
        <div class="brand">
          ${
            logoSrc
              ? `<img class="logo-img" src="${escapeHtml(logoSrc)}" alt="Mobilus" />`
              : `<div class="logo">SL2</div>`
          }
          <div>
            <div class="kicker">Mobilus Smart Locker</div>
            <h1>${escapeHtml(labels.title)}</h1>
            ${
              labels.generatedAtValue
                ? `<div class="generated">${escapeHtml(
                    labels.generatedAt,
                  )}: ${escapeHtml(labels.generatedAtValue)}</div>`
                : ""
            }
          </div>
        </div>
        <div class="meta">
          <div class="locker">${escapeHtml(lockerName)}</div>
          ${addressLine}
          <div class="count">${escapeHtml(labels.total)}: ${total}</div>
        </div>
      </div>
      ${sections}
      <div class="foot">${escapeHtml(labels.title)} &middot; ${escapeHtml(
        lockerName,
      )}</div>
    </div>
    <script>window.onload = function () { window.print() }</script>
  </body>
</html>`

  const win = window.open("", "_blank")
  if (!win) {
    // Pop-up blocked — surface nothing here; the caller logs context.
    return
  }
  win.document.open()
  win.document.write(html)
  win.document.close()
}
